<script lang="ts">
	import LoadingTable from '$lib/component/navigation/LoadingTable.svelte';
	import { cssInput, glassDesignStyle } from '$lib/helper/color.js';
	import { apiStore, getApiBaseUrl, initPublicApi } from '$lib/stores/api';
	import { t } from '$lib/stores/i18n';
	import { onMount } from 'svelte';

	interface Props {
		value: string | undefined;
		readonly?: boolean;
		glassDesign?: boolean;
	}

	let { value = $bindable(), readonly = false, glassDesign = false }: Props = $props();

	let id = 'editorjs-' + Math.random().toString(36).substring(2, 15);

	let loading = $state(!readonly);

	function convertBasicTextAreaToJson(value: string | undefined) {
		if (!value) {
			return JSON.stringify({ time: 0, blocks: [] });
		}
		const lines = value.split('\n');
		const blocks = lines.map((line) => ({
			type: 'paragraph',
			data: {
				text: line
			}
		}));
		return JSON.stringify({ time: 0, blocks });
	}

	let editor;
	let editorLoaded = $state(false);
	let tokenLoaded = $state(false);
	let apiUploadUrl = $state('');
	let elt: HTMLElement | null = $state(null);

	onMount(() => {
		if (readonly) {
			tokenLoaded = true;
		} else {
			if (!$apiStore) initPublicApi();
			$apiStore?.get('/upload/public-token', {}).then((response) => {
				apiUploadUrl = getApiBaseUrl() + response?.data?.url;
				tokenLoaded = true;
			});
		}
	});

	async function loadEditor() {
		const id = elt?.id;
		if (!id) {
			return;
		}
		const {
			EditorJS,
			DragDrop,
			Embed,
			Header,
			Quote,
			Checklist,
			Table,
			Delimiter,
			Alert,
			ColorPicker,
			EditorjsList,
			Marker,
			ImageTool,
			AttachesTool
		} = await import('./richtextarea.ts');

		editor = new EditorJS({
			holder: id,
			// readOnly: readonly, // TODO: wait for fix https://github.com/codex-team/editor.js/issues/2772
			data: JSON.parse(value?.startsWith('{') ? value : convertBasicTextAreaToJson(value)),

			//inlineToolbar: ['link', 'marker', 'bold', 'italic'],
			//inlineToolbar: true,
			onReady: () => {
				loading = false;
				new DragDrop(editor, '2px solid #fff');
				if (readonly) {
					editor.readOnly.toggle(true); // TODO: wait for fix https://github.com/codex-team/editor.js/issues/2772 (then remove this line, and uncomment the line above)
					// Remove margin-right from all .codex-editor__redactor elements
					const elements = document.querySelectorAll('.codex-editor__redactor');
					elements.forEach((element) => {
						element.style.marginRight = '0px';
					});

					requestAnimationFrame(() => {
						elt?.querySelectorAll('a').forEach((a) => {
							a.setAttribute('target', '_blank');
							a.setAttribute('rel', 'noopener noreferrer');
						});
					});
				}
			},
			onChange: (api, event) => {
				editor
					.save()
					.then((outputData) => {
						value = JSON.stringify(outputData);
					})
					.catch((error) => {
						console.log('Saving failed: ', error);
					});
			},
			minHeight: readonly ? 0 : 300,
			placeholder: !readonly ? $t('richtext.placeholder') : '',
			tools: {
				embed: {
					class: Embed,
					inlineToolbar: true
				},
				Marker: {
					class: Marker,
					shortcut: 'CMD+SHIFT+M'
				},
				header: {
					class: Header,
					config: {
						placeholder: 'Header',
						levels: [1, 2, 3, 4],
						defaultLevel: 1
					}
				},
				image: {
					class: ImageTool,
					config: {
						endpoints: {
							byFile: apiUploadUrl
							// byUrl: 'http://localhost:8008/fetchUrl' // Your endpoint that provides uploading by Url
						}
					}
				},

				attaches: {
					class: AttachesTool,
					config: {
						field: 'image',
						endpoint: apiUploadUrl
					}
				},
				ColorPicker: {
					class: ColorPicker
				},
				checklist: {
					class: Checklist,
					inlineToolbar: true
				},
				list: {
					class: EditorjsList,
					inlineToolbar: true,
					config: {
						defaultStyle: 'unordered'
					}
				},
				delimiter: Delimiter,
				quote: Quote,
				table: Table,
				alert: Alert
			}
		});
	}

	$effect(() => {
		if (!editorLoaded && tokenLoaded && elt) {
			editorLoaded = true;
			loadEditor();
		}
	});
</script>

{#if loading}
	<LoadingTable />
	<LoadingTable />
	<LoadingTable />
{/if}
<div
	{id}
	class="{glassDesign
		? cssInput('', glassDesignStyle)
		: ''} editor-container transition-all duration-1000 text-wrap {loading
		? 'h-0 opacity-0 overflow-hidden max-w-full '
		: ''}"
	style={!readonly ? 'color: #000000 !important; background-color: #ffffff77 !important;' : ''}
	bind:this={elt}
></div>

<style>
	:global(.editor-container h1) {
		font-size: 2.5rem /* 30px */;
		line-height: 2.25rem /* 36px */;
		font-weight: 800;
	}
	:global(.editor-container h2) {
		font-size: 1.8rem /* 20px */;
		line-height: 1.75rem /* 28px */;
		font-weight: 800;
	}

	:global(.editor-container h3) {
		font-size: 1.5rem /* 18px */;
		line-height: 1.75rem /* 28px */;
		font-weight: 600;
	}

	:global(.editor-container h4) {
		font-size: 1.25rem /* 16px */;
		line-height: 1.5rem /* 24px */;
		font-weight: 600;
	}

	:global(.editor-container .embed-tool__caption) {
		display: none;
	}

	:global(.editor-container .cdx-quote__text) {
		border-width: 0px;
		border-left-width: 4px;
		box-shadow: none;
		background-color: var(--widget-background-color);
		border-color: var(--widget-primary-color);
		opacity: 0.5;
		font-style: italic;
		font-family: 'Margarine', cursive;
		min-height: 0px;
	}

	:global(.editor-container .cdx-quote__caption) {
		display: none;
	}

	:global(.editor-container .cdx-quote__caption) {
		display: none;
	}

	:global(.editor-container .cdx-warning__title) {
		display: none;
	}
	:global(.editor-container .cdx-simple-image__caption) {
		display: none;
	}
</style>
